import React from 'react';
import { IProcurementOrder, IProcurementOrderItem } from '../../../../models/Procurement';
import { Shimmer } from '@fluentui/react';
import CurrencyDisplay from '../../../Currency/CurrencyDisplay';
import { useGetProcurementOrderCostQuery } from '../../../../store/endpoints/ProcurementEndpoints';

interface IOrderItemsTableProps {
    order: IProcurementOrder;
    onChange: (updatedOrder: IProcurementOrder) => void;
    showTotals?: boolean;
}

const OrderItemsTable: React.FC<IOrderItemsTableProps> = (props) => {
    const costs = useGetProcurementOrderCostQuery({orderId: props.order.id});
    const [subtotal, setSubtotal] = React.useState<number>();

    React.useEffect(() => {
        if (costs.data) {
            const newSubtotal = props.order.items.reduce((sum, item) => {
                const itemCost = costs.data?.items[item.id]?.cost || 0;
                return sum + (itemCost * item.quantity);
            }, 0);
            setSubtotal(newSubtotal);
        }
    }, [costs.data, props.order.items]);

    return (
        <div>
            <table className='data-table'>
                <thead>
                    <tr>
                        <th>Manufacturer</th>
                        <th>Item</th>
                        <th>Cost</th>
                        <th>Quantity</th>
                        <th>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {props.order.items.map((item, index) => (
                        <OrderItemTableRow key={index} item={item} cost={costs.data?.items[item.id]?.cost} loadingCost={costs.isFetching} />
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={4} style={{ textAlign: 'right', fontWeight: props.showTotals ? 'normal' : 'bold' }}>Subtotal:</td>
                        <td style={{ fontWeight: props.showTotals ? 'normal' : 'bold' }}>
                            {costs.isFetching ? <Shimmer /> : <CurrencyDisplay value={subtotal} />}
                        </td>
                    </tr>
                    {props.showTotals && props.order.shipping ? (
                        <tr>
                            <td colSpan={4} style={{ textAlign: 'right' }}>Shipping:</td>
                            <td>
                                <CurrencyDisplay value={props.order.shipping || 0} />
                            </td>
                        </tr>
                    ) : null}
                    {props.showTotals && props.order.tax ? (
                        <>
                            <tr>
                                <td colSpan={4} style={{ textAlign: 'right' }}>Tax:</td>
                                <td>
                                    <CurrencyDisplay value={props.order.tax || 0} />
                                </td>
                            </tr>
                            </>
                    ) : null}
                    {props.showTotals ? (
                        <tr>
                            <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total:</td>
                            <td style={{ fontWeight: 'bold' }}>
                                <CurrencyDisplay value={(subtotal || 0) + (props.order.tax || 0)} />
                            </td>
                        </tr>
                    ) : null}
                </tfoot>
            </table>
        </div>
    );
}

interface IOrderItemTableRowProps {
    item: IProcurementOrderItem;
    loadingCost?: boolean;
    cost?: number;
}

const OrderItemTableRow: React.FC<IOrderItemTableRowProps> = (props) => {
    return (
        <tr>
            <td>{props.item.productManufacturer}</td>
            <td>{props.item.productName}</td>
            <td>{props.loadingCost ? <Shimmer /> : <CurrencyDisplay value={props.item.cost || props.cost || 0} />}</td>
            <td>{props.item.quantity}</td>
            <td>{props.loadingCost ? <Shimmer /> : <CurrencyDisplay value={(props.item.cost || props.cost || 0) * props.item.quantity} />}</td>
        </tr>
    );
}

export default OrderItemsTable;